import { cva, type VariantProps } from 'class-variance-authority';
import { type ReactNode } from 'react';

import { cn } from '../../lib/cn';
import type { StatusTone } from '../../lib/status';
import { Readout } from '../Readout/Readout';

const stateField = cva(
  [
    'flex w-full flex-wrap items-baseline gap-x-3 gap-y-2',
    'rounded-band px-4 pb-4 pt-3.5',
    'border-b-[3px] border-canvas',
  ],
  {
    variants: {
      tone: {
        success: 'bg-status-success text-status-success-fg',
        warning: 'bg-status-warning text-status-warning-fg',
        danger: 'bg-status-danger text-status-danger-fg',
        info: 'bg-status-info text-status-info-fg',
        neutral: 'bg-status-neutral text-status-neutral-fg',
        'neutral-strong': 'bg-status-neutral-strong text-status-neutral-strong-fg',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export type StateFieldVariants = VariantProps<typeof stateField>;

export interface StateFieldProps {
  /**
   * From `WORKER_STATE_TONE`, never chosen per screen. Filled from the STATUS
   * palette and never from brand blue: brand blue on this band would make
   * identity mean "you are on the clock", which is the one thing it must never
   * say. (`info` is legitimate here — it is a status field, not the brand.)
   */
  readonly tone: StatusTone;
  /** The state word at `headline` scale. Already translated. */
  readonly word: ReactNode;
  /** The state's ONE number, if it has one. */
  readonly value?: ReactNode;
  /**
   * `duration` (66 px) for an elapsed time — the thing that only this state
   * has. `clock` (40 px) for a wall time, because "22 h 00" and "03 h 12" at
   * the same size are the same visual gesture at arm's length, and the home
   * screen's whole job is to make them different.
   */
  readonly valueScale?: 'duration' | 'clock';
  /** A screen-reader expansion of `value`: "3 heures 12 minutes". */
  readonly valueSrLabel?: string;
  /** A 20 px glyph beside the word. Decorative — the word carries the meaning. */
  readonly icon?: ReactNode;
  /** A short line under the number: the site, the shift window. */
  readonly detail?: ReactNode;
  /**
   * An end-aligned slot on the word's own line — a chip, a count, a quiet
   * clock. The band's colour and word answer *what state am I in*; the cap is
   * for the one qualifier that belongs beside that answer rather than under
   * the number (a queued-punch count, the site's abbreviation).
   *
   * It sits on the status field, so anything placed here must read on a solid
   * fill: `currentColor` inherits the band's own `-fg` token, which is the
   * pair the theme measured. Omit it and the band is drawn exactly as before.
   *
   * **At 360 px the cap moves under the number.** A 25 px state word and a
   * chip sharing one line leave the chip about 90 px on the smallest supported
   * handset, which is where a two-word French qualifier wraps mid-word or
   * squeezes the word it is meant to qualify. Below 361 px it takes its own
   * full-width line beneath the readout instead — the reading order a screen
   * reader already followed, made visible.
   */
  readonly cap?: ReactNode;
  /**
   * The band's own ARIA role. Defaults to `status`, which is what makes a
   * state change under a worker's thumb get announced — see the note below.
   *
   * A parent that already owns a live region around this band (so it is not
   * itself double-announced) can pass `'none'` to render the band inert; the
   * parent's region is then the only thing a screen reader speaks from.
   */
  readonly role?: 'status' | 'alert' | 'none';
  readonly className?: string;
}

/**
 * THE SIGNATURE COMPONENT, grafted from Direction C. The worker's home state.
 *
 * A full-bleed band where **colour, word and number change together**, so "am I
 * on the clock?" is answered before the phone reaches eye level. One component
 * renders all six home states plus every recovery state by swapping one fill
 * and one word — which is why it beat six bespoke screens.
 *
 * Use the band for a state that INTERRUPTS: off duty, offline and queued, an
 * unclosed shift, a refused device. Use an in-panel `Readout` for the normal
 * working home screen — a full-bleed alarm colour that is on screen every night
 * stops being read.
 *
 * `role="status"` on purpose: when the state changes under a worker's thumb,
 * the change is announced.
 */
/**
 * The one place the 360 px rule is written down.
 *
 * The band is a wrapping flex row: at 361 px and up the word takes the space
 * that is left and the cap sits end-aligned beside it, exactly as before. At
 * 360 px and below the cap is given its own full-width line and an explicit
 * order, so it lands under the readout instead of squeezing the state word.
 */
const NARROW_ORDER = {
  word: '[@media(max-width:360px)]:order-1',
  value: '[@media(max-width:360px)]:order-2',
  cap: '[@media(max-width:360px)]:order-3 [@media(max-width:360px)]:w-full',
  detail: '[@media(max-width:360px)]:order-4',
} as const;

export function StateField({
  tone,
  word,
  value,
  valueScale = 'duration',
  valueSrLabel,
  icon,
  detail,
  cap,
  role = 'status',
  className,
}: StateFieldProps) {
  return (
    <div
      {...(role !== 'none' ? { role } : {})}
      className={cn(stateField({ tone }), className)}
    >
      <div className={cn('flex min-w-0 flex-1 items-center gap-2', NARROW_ORDER.word)}>
        {icon !== undefined ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {icon}
          </span>
        ) : null}
        <p className="text-headline">{word}</p>
      </div>

      {cap !== undefined ? (
        <span className={cn('flex shrink-0 items-center gap-2', NARROW_ORDER.cap)}>{cap}</span>
      ) : null}

      {value !== undefined ? (
        <Readout
          scale={valueScale === 'duration' ? 'duration' : 'clock'}
          tone="onField"
          className={cn('w-full', NARROW_ORDER.value)}
          {...(valueSrLabel !== undefined ? { srValue: valueSrLabel } : {})}
        >
          {value}
        </Readout>
      ) : null}

      {detail !== undefined ? (
        <p className={cn('w-full text-body-compact', NARROW_ORDER.detail)}>{detail}</p>
      ) : null}
    </div>
  );
}
